import { lazy, Suspense } from "react";
import AuthGuard from "../components/common/AuthGuard";
import AdminGuard from "../components/common/AdminGuard";
import BuyerGuard from "../components/common/BuyerGuard";
import SellerGuard from "../components/common/SellerGuard";
import StoreSellerAccessGuard from "../components/common/StoreSellerAccessGuard";
import Header from "../components/layout/Header";
import FullPageLoader from "../components/common/FullPageLoader";

// Lazy page imports
const Login = lazy(() => import("../pages/Login"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const QuotesList = lazy(() => import("../pages/Quotes"));
const NewQuote = lazy(() => import("../pages/Quotes/New"));
const QuoteDetails = lazy(() => import("../pages/Quotes/[id]"));
const NotFound = lazy(() => import("../pages/NotFound"));
const PhoneRegistration = lazy(() => import("../pages/PhoneRegistration"));
const PhoneVerification = lazy(() => import("../pages/PhoneVerification"));
const NewStore = lazy(() => import("../pages/Stores/New"));
const StoresList = lazy(() => import("../pages/Stores"));
const AdminQuotationsList = lazy(() => import("../pages/AdminQuotations"));
const EditStore = lazy(() => import("../pages/Stores/Edit"));
const Profile = lazy(() => import("../pages/Profile"));
const ProjectList = lazy(() => import("../pages/Project"));
const NewProject = lazy(() => import("../pages/Project/New"));
const EditProject = lazy(() => import("../pages/Project/Edit"));
const StoreSignup = lazy(() => import("../pages/StoreSignup"));
const StoreRegistration = lazy(() => import("../pages/StoreRegistration"));
const SellerQuotationsList = lazy(() => import("../pages/Seller/Quotations"));
const SellerQuotationDetails = lazy(() => import("../pages/Seller/Quotations/[id]"));
const SellerDashboard = lazy(() => import("../pages/Seller/Dashboard"));
const SellerProfile = lazy(() => import("../pages/Seller/Profile"));
const SellerStore = lazy(() => import("../pages/Seller/Store"));

// Suspense wrapper
const withSuspense = (Component) => (
  <Suspense fallback={<FullPageLoader />}>
    <Component />
  </Suspense>
);

// Route definitions
export const routes = [
  {
    path: "login",
    element: withSuspense(Login),
  },
  {
    path: "store-signup",
    element: withSuspense(StoreSignup),
  },
  {
    path: "store-registration",
    element: withSuspense(StoreRegistration),
  },
  {
    // Todas as rotas abaixo exigem sessão + telefone verificado (AuthGuard);
    // os guards de grupo apenas decidem o acesso por perfil.
    element: <AuthGuard />,
    children: [
      {
        path: "/phone-registration",
        element: withSuspense(PhoneRegistration),
      },
      {
        path: "/phone-verification",
        element: withSuspense(PhoneVerification),
      },
      {
        path: "/",
        element: <Header />,
        children: [
          {
            element: <BuyerGuard />,
            children: [
              {
                index: true,
                element: withSuspense(Dashboard),
              },
              {
                path: "profile",
                element: withSuspense(Profile),
              },
              {
                path: "projects",
                children: [
                  { index: true, element: withSuspense(ProjectList) },
                  { path: "new", element: withSuspense(NewProject) },
                  { path: ":id", element: withSuspense(EditProject) },
                ],
              },
              {
                path: "quotes",
                children: [
                  { index: true, element: withSuspense(QuotesList) },
                  { path: "new", element: withSuspense(NewQuote) },
                  { path: ":id", element: withSuspense(QuoteDetails) },
                ],
              },
            ],
          },
          {
            element: <SellerGuard />,
            children: [
              { path: "seller/dashboard", element: withSuspense(SellerDashboard) },
              { path: "seller/quotations", element: withSuspense(SellerQuotationsList) },
              { path: "seller/profile", element: withSuspense(SellerProfile) },
              { path: "seller/store", element: withSuspense(SellerStore) },
            ],
          },
          {
            // Deep link da cotação do vendedor (com ?storeId): controle de acesso à loja
            element: <StoreSellerAccessGuard />,
            children: [
              {
                path: "seller/quotations/:id",
                element: withSuspense(SellerQuotationDetails),
              },
            ],
          },
          {
            element: <AdminGuard />,
            children: [
              {
                path: "stores",
                children: [
                  { index: true, element: withSuspense(StoresList) },
                  { path: "new", element: withSuspense(NewStore) },
                  { path: ":id/edit", element: withSuspense(EditStore) },
                ],
              },
              { path: "admin/quotations", element: withSuspense(AdminQuotationsList) },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: withSuspense(NotFound) },
];
